/**
 * mobile-menu-e2.js -- Enrichment custom script (not a Sleek file).
 * 2026-10-04: mobile menu "Option E2" (snippets/mobile-menu-e2.liquid,
 * sections/mobile-menu-e2.liquid, plan in
 * strategy/mobile-menu-e2-plan-2026-10-04.md).
 *
 * Takes over the header's existing hamburger buttons (.menu-drawer-button)
 * with a capture-phase click listener, so the original <menu-drawer> is
 * never opened while this element is on the page. No E2 element (toggle
 * off / section hidden / this file failing to load) = the hamburgers work
 * exactly as before.
 *
 * Owner notes implemented here: 200-250ms slides between screens, swipe
 * right on a section screen goes back, focus trapped while open, Esc and
 * the close buttons dismiss it, focus returns to the hamburger, section
 * rows carry aria-expanded, always reopens on the main screen, predictive
 * search grouped Products / Ingredients and guides with the matched letters
 * bolded, recent searches kept in localStorage, points from the shared
 * EnrichmentRewardsState (assets/rewards-state.js).
 */
(function () {
  if (customElements.get('mobile-menu-e2')) return;

  var SLIDE_MS = 220;
  var OPEN_MS = 250;
  var DESKTOP_MQ = '(min-width: 1024px)';
  var RECENT_KEY = 'enrichment_recent_searches';
  var RECENT_MAX = 6;
  var HAMBURGER_SELECTOR = '.menu-drawer-button, [aria-controls="MobileMenuE2"]';
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function wait(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, reduceMotion() ? 0 : ms);
    });
  }
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /** Bold every case-insensitive occurrence of each query word. */
  function highlight(text, query) {
    var terms = query
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .sort(function (a, b) {
        return b.length - a.length;
      })
      .map(escapeRegExp);
    if (!terms.length) return escapeHtml(text);
    var re = new RegExp('(' + terms.join('|') + ')', 'gi');
    var out = '';
    var last = 0;
    String(text).replace(re, function (match, _g, offset) {
      out += escapeHtml(text.slice(last, offset)) + '<b>' + escapeHtml(match) + '</b>';
      last = offset + match.length;
      return match;
    });
    return out + escapeHtml(text.slice(last));
  }

  /* 2026-10-07 owner: iOS Safari ignores overflow:hidden on html/body for
     touch scrolling, so the page behind the menu could still be dragged and
     seen (most often under search results, with the keyboard up). Pin the
     body in place while the menu is open and restore the scroll on close. */
  var lockedScrollY = 0;
  function lockPage() {
    var root = document.documentElement;
    if (root.classList.contains('mm-e2-lock')) return;
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    root.classList.add('mm-e2-lock');
    document.body.style.top = -lockedScrollY + 'px';
  }
  function unlockPage() {
    var root = document.documentElement;
    if (!root.classList.contains('mm-e2-lock')) return;
    root.classList.remove('mm-e2-lock');
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
  }

  function readRecent() {
    try {
      var list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list) ? list.filter(function (t) { return typeof t === 'string' && t.trim().length >= 3; }) : [];
    } catch (e) {
      return [];
    }
  }
  function writeRecent(list) {
    try {
      if (list.length) localStorage.setItem(RECENT_KEY, JSON.stringify(list));
      else localStorage.removeItem(RECENT_KEY);
    } catch (e) {
      /* private mode / storage blocked -- recent searches are a convenience only */
    }
  }

  class MobileMenuE2 extends HTMLElement {
    connectedCallback() {
      // Theme editor re-renders leave the previous instance (moved to <body>)
      // behind -- keep only the newest.
      document.querySelectorAll('mobile-menu-e2').forEach((el) => {
        if (el !== this) el.remove();
      });
      // Escape any transformed/sticky ancestor so position:fixed covers the
      // viewport. appendChild re-fires connectedCallback; the guard below
      // makes that second pass the one that initialises.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }
      if (this._ready) return;
      this._ready = true;

      this.panel = this.querySelector('.mm-e2__panel');
      this.viewport = this.querySelector('.mm-e2__viewport');
      this.mainScreen = this.querySelector('[data-mm-e2-screen="main"]');
      this.account = this.querySelector('[data-mm-e2-account]');
      this.searchView = this.querySelector('[data-mm-e2-search]');
      this.searchForm = this.querySelector('[data-mm-e2-search-form]');
      this.searchInput = this.querySelector('[data-mm-e2-search-input]');
      this.resultsEl = this.querySelector('[data-mm-e2-results]');
      this.recentEl = this.querySelector('[data-mm-e2-recent]');
      this.recentList = this.querySelector('[data-mm-e2-recent-list]');
      this.pointsEl = this.querySelector('[data-mm-e2-points]');
      this.currentScreen = this.mainScreen;
      this.cache = new Map();

      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', this.onClick.bind(this));
      this.searchInput.addEventListener('input', this.onSearchInput.bind(this));
      this.searchForm.addEventListener('submit', this.onSearchSubmit.bind(this));
      this.bindSwipe();
      this.takeOverHamburgers();

      this.desktopMql = window.matchMedia(DESKTOP_MQ);
      var onBreakpoint = () => {
        if (this.desktopMql.matches && this.isOpen) this.close({ returnFocus: false, immediate: true });
      };
      if (this.desktopMql.addEventListener) this.desktopMql.addEventListener('change', onBreakpoint);

      if (window.Shopify && Shopify.designMode) this.bindThemeEditor();
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    /* ---------- hamburger take-over ---------- */
    takeOverHamburgers() {
      var retarget = () => {
        document.querySelectorAll('.menu-drawer-button').forEach((btn) => {
          if (this.contains(btn)) return;
          btn.setAttribute('aria-controls', 'MobileMenuE2');
          btn.setAttribute('aria-haspopup', 'dialog');
          btn.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
          if (!btn.getAttribute('aria-label')) btn.setAttribute('aria-label', 'Open menu');
        });
      };
      retarget();
      if (window.__mmE2HamburgerBound) return;
      window.__mmE2HamburgerBound = true;
      document.addEventListener(
        'click',
        function (event) {
          var btn = event.target.closest && event.target.closest(HAMBURGER_SELECTOR);
          var menu = document.getElementById('MobileMenuE2');
          if (!btn || !menu || menu.contains(btn) || !menu._ready) return;
          if (window.matchMedia(DESKTOP_MQ).matches) return;
          event.preventDefault();
          event.stopPropagation();
          menu.isOpen ? menu.close() : menu.open(btn);
        },
        true
      );
    }

    setHamburgerExpanded(value) {
      document.querySelectorAll('[aria-controls="MobileMenuE2"]').forEach((btn) => {
        if (!this.contains(btn)) btn.setAttribute('aria-expanded', value ? 'true' : 'false');
      });
    }

    /* ---------- open / close ---------- */
    open(trigger) {
      if (this.isOpen || this._closing) return;
      this.trigger = trigger || document.activeElement;
      this.resetToMain();
      this.hidden = false;
      lockPage();
      this.panel.scrollTop = 0;
      // reflow so the slide-in transition runs from the off-canvas state
      void this.panel.offsetWidth;
      this.classList.add('is-open');
      this.setHamburgerExpanded(true);
      document.addEventListener('keydown', this.onKeydown, true);
      var first = this.mainScreen.querySelector('[data-mm-e2-close]');
      if (first) first.focus({ preventScroll: true });
      this.updatePoints();
    }

    close(options) {
      options = options || {};
      if (!this.isOpen) return Promise.resolve();
      this._closing = true;
      this.classList.remove('is-open');
      this.setHamburgerExpanded(false);
      document.removeEventListener('keydown', this.onKeydown, true);
      var done = () => {
        this.hidden = true;
        this._closing = false;
        unlockPage();
        this.resetToMain();
      };
      var returnFocus = options.returnFocus !== false;
      if (returnFocus && this.trigger && document.contains(this.trigger)) {
        this.trigger.focus({ preventScroll: true });
      }
      if (options.immediate) {
        done();
        return Promise.resolve();
      }
      return wait(OPEN_MS).then(done);
    }

    /** "Always open on the main screen" -- reset every screen + search. */
    resetToMain() {
      this.classList.remove('is-animating');
      this.querySelectorAll('[data-mm-e2-screen]').forEach((screen) => {
        var isMain = screen === this.mainScreen;
        screen.classList.toggle('is-active', isMain);
        screen.classList.remove('is-left', 'is-dragging');
        screen.style.transform = '';
        screen.hidden = !isMain;
        screen.inert = !isMain;
        var scroller = screen.querySelector('.mm-e2__scroll');
        if (scroller) scroller.scrollTop = 0;
      });
      this.querySelectorAll('[data-mm-e2-open]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      this.currentScreen = this.mainScreen;
      this.hideSearch(true);
    }

    /* ---------- screens ---------- */
    openScreen(id, rowButton) {
      var screen = this.querySelector('[data-mm-e2-screen="' + id + '"]');
      if (!screen || this._sliding) return;
      this._sliding = true;
      this.activeRow = rowButton;
      screen.hidden = false;
      screen.inert = false;
      screen.classList.remove('is-active', 'is-left');
      var scroller = screen.querySelector('.mm-e2__scroll');
      if (scroller) scroller.scrollTop = 0;
      void screen.offsetWidth;
      this.classList.add('is-animating');
      this.mainScreen.classList.remove('is-active');
      this.mainScreen.classList.add('is-left');
      screen.classList.add('is-active');
      if (rowButton) rowButton.setAttribute('aria-expanded', 'true');
      this.currentScreen = screen;
      var back = screen.querySelector('[data-mm-e2-back]');
      if (back) back.focus({ preventScroll: true });
      wait(SLIDE_MS).then(() => {
        this.classList.remove('is-animating');
        this.mainScreen.hidden = true;
        this.mainScreen.inert = true;
        this._sliding = false;
      });
    }

    back() {
      var screen = this.currentScreen;
      if (!screen || screen === this.mainScreen || this._sliding) return;
      this._sliding = true;
      this.mainScreen.hidden = false;
      this.mainScreen.inert = false;
      void this.mainScreen.offsetWidth;
      this.classList.add('is-animating');
      screen.classList.remove('is-dragging');
      this.mainScreen.classList.remove('is-dragging');
      screen.style.transform = '';
      this.mainScreen.style.transform = '';
      screen.classList.remove('is-active');
      this.mainScreen.classList.remove('is-left');
      this.mainScreen.classList.add('is-active');
      this.currentScreen = this.mainScreen;
      var row = this.activeRow;
      if (row) {
        row.setAttribute('aria-expanded', 'false');
        row.focus({ preventScroll: true });
      }
      wait(SLIDE_MS).then(() => {
        this.classList.remove('is-animating');
        screen.hidden = true;
        screen.inert = true;
        this._sliding = false;
      });
    }

    /* ---------- swipe right = back ---------- */
    bindSwipe() {
      var start = null;
      var dragging = false;
      var width = 1;
      this.viewport.addEventListener(
        'touchstart',
        (e) => {
          if (this.currentScreen === this.mainScreen || this._sliding || e.touches.length !== 1) return;
          start = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
          dragging = false;
          width = this.viewport.clientWidth || 1;
        },
        { passive: true }
      );
      this.viewport.addEventListener(
        'touchmove',
        (e) => {
          if (!start) return;
          var dx = e.touches[0].clientX - start.x;
          var dy = e.touches[0].clientY - start.y;
          if (!dragging) {
            if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
              start = null; // vertical scroll wins
              return;
            }
            if (dx > 10 && dx > Math.abs(dy)) {
              dragging = true;
              this.mainScreen.hidden = false;
              this.currentScreen.classList.add('is-dragging');
              this.mainScreen.classList.add('is-dragging');
            } else {
              return;
            }
          }
          var x = Math.max(0, dx);
          this.currentScreen.style.transform = 'translateX(' + x + 'px)';
          this.mainScreen.style.transform = 'translateX(' + (-30 + (30 * x) / width) + '%)';
        },
        { passive: true }
      );
      var end = (e) => {
        if (!start) return;
        var touch = e.changedTouches && e.changedTouches[0];
        var dx = touch ? touch.clientX - start.x : 0;
        var velocity = dx / Math.max(1, Date.now() - start.t);
        var wasDragging = dragging;
        start = null;
        dragging = false;
        if (!wasDragging) return;
        if (dx > width * 0.25 || velocity > 0.45) {
          this.back();
        } else {
          // snap back
          var screen = this.currentScreen;
          this.classList.add('is-animating');
          screen.classList.remove('is-dragging');
          this.mainScreen.classList.remove('is-dragging');
          screen.style.transform = '';
          this.mainScreen.style.transform = '';
          wait(SLIDE_MS).then(() => {
            this.classList.remove('is-animating');
            if (this.currentScreen !== this.mainScreen) this.mainScreen.hidden = true;
          });
        }
      };
      this.viewport.addEventListener('touchend', end);
      this.viewport.addEventListener('touchcancel', end);
    }

    /* ---------- clicks ---------- */
    onClick(event) {
      var t = event.target;
      var el;
      if ((el = t.closest('[data-mm-e2-close]'))) {
        event.preventDefault();
        this.close();
      } else if ((el = t.closest('[data-mm-e2-open]'))) {
        event.preventDefault();
        this.openScreen(el.getAttribute('data-mm-e2-open'), el);
      } else if (t.closest('[data-mm-e2-back]')) {
        event.preventDefault();
        this.back();
      } else if (t.closest('[data-mm-e2-search-open]')) {
        event.preventDefault();
        this.showSearch();
      } else if (t.closest('[data-mm-e2-search-cancel]')) {
        event.preventDefault();
        this.hideSearch();
      } else if (t.closest('[data-mm-e2-recent-clear]')) {
        event.preventDefault();
        writeRecent([]);
        this.renderRecent();
        this.searchInput.focus();
      } else if ((el = t.closest('[data-mm-e2-chip]'))) {
        event.preventDefault();
        this.searchInput.value = el.getAttribute('data-mm-e2-chip');
        this.saveRecent(this.searchInput.value);
        this.runSearch();
        this.searchInput.focus();
      } else if ((el = t.closest('[data-mm-e2-see-all]'))) {
        // 2026-10-07 owner: only a deliberate search (see all, Enter, chip)
        // is remembered. Tapping a live result used to save whatever was
        // half-typed ("cer"), which filled Recent with fragments.
        this.saveRecent(this.searchInput.value);
      } else if (t.closest('[data-mm-e2-cart]')) {
        // Cart drawer (its own aria-controls listener) takes over from here.
        if (document.getElementById('CartDrawer')) this.close({ returnFocus: false, immediate: true });
      }
    }

    /* ---------- focus trap + Esc ---------- */
    focusables() {
      return Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return !el.closest('[inert], [hidden]') && el.getClientRects().length > 0;
      });
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      var items = this.focusables();
      if (!items.length) {
        event.preventDefault();
        return;
      }
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      if (!this.panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ---------- search ---------- */
    showSearch() {
      this.searchView.hidden = false;
      this.searchView.inert = false;
      this.viewport.inert = true;
      this.account.inert = true;
      // focus synchronously inside the tap so iOS raises the keyboard
      this.searchInput.focus({ preventScroll: true });
      this.renderRecent();
      if (this.searchInput.value.trim()) this.runSearch();
      requestAnimationFrame(() => this.searchView.classList.add('is-visible'));
      this.fitSearchToViewport(true);
    }

    // 2026-10-07 owner: with the keyboard up, iOS pans the layout viewport,
    // so a search view sized to the panel could slide and expose the page.
    // Pin it to the visual viewport instead, so it always fills the screen.
    fitSearchToViewport(on) {
      var vv = window.visualViewport;
      if (!vv) return;
      if (!this._fitSearch) {
        this._fitSearch = () => {
          if (!this.searchView || this.searchView.hidden) return;
          this.searchView.style.position = 'fixed';
          this.searchView.style.top = vv.offsetTop + 'px';
          this.searchView.style.left = vv.offsetLeft + 'px';
          this.searchView.style.width = vv.width + 'px';
          this.searchView.style.height = vv.height + 'px';
          this.searchView.style.bottom = 'auto';
          this.searchView.style.right = 'auto';
        };
      }
      vv.removeEventListener('resize', this._fitSearch);
      vv.removeEventListener('scroll', this._fitSearch);
      if (on) {
        vv.addEventListener('resize', this._fitSearch);
        vv.addEventListener('scroll', this._fitSearch);
        this._fitSearch();
      } else {
        ['position', 'top', 'left', 'width', 'height', 'bottom', 'right'].forEach((k) => {
          this.searchView.style[k] = '';
        });
      }
    }

    hideSearch(immediate) {
      if (!this.searchView || this.searchView.hidden) return;
      this.searchView.classList.remove('is-visible');
      this.fitSearchToViewport(false);
      this.viewport.inert = false;
      this.account.inert = false;
      var finish = () => {
        this.searchView.hidden = true;
        this.searchView.inert = true;
      };
      if (immediate === true) {
        finish();
        this.searchInput.value = '';
        this.resultsEl.innerHTML = '';
        return;
      }
      var trigger = this.mainScreen.querySelector('[data-mm-e2-search-open]');
      if (trigger && this.currentScreen === this.mainScreen) trigger.focus({ preventScroll: true });
      wait(200).then(finish);
    }

    saveRecent(term) {
      term = (term || '').trim();
      if (term.length < 3) return;
      var list = readRecent().filter(function (t) {
        return t.toLowerCase() !== term.toLowerCase();
      });
      list.unshift(term);
      writeRecent(list.slice(0, RECENT_MAX));
    }

    renderRecent() {
      var list = readRecent();
      this.recentEl.hidden = list.length === 0;
      this.recentList.innerHTML = list
        .map(function (term) {
          return (
            '<li><button type="button" class="mm-e2__chip" data-mm-e2-chip="' +
            escapeHtml(term) +
            '">' +
            escapeHtml(term) +
            '</button></li>'
          );
        })
        .join('');
    }

    onSearchInput() {
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this.runSearch(), 160);
    }

    onSearchSubmit(event) {
      var q = this.searchInput.value.trim();
      if (!q) {
        event.preventDefault();
        return;
      }
      this.saveRecent(q);
    }

    searchPageUrl(q) {
      var url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('type', 'product,article,page');
      url.searchParams.set('options[prefix]', 'last');
      return url.pathname + url.search;
    }

    runSearch() {
      var q = this.searchInput.value.trim();
      if (this._abort) this._abort.abort();
      if (!q) {
        this.resultsEl.innerHTML = '';
        this.resultsEl.removeAttribute('aria-busy');
        return;
      }
      var url = new URL((this.dataset.suggestUrl || '/search/suggest') + '.json', window.location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][prefix]', 'last');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      var key = url.toString();
      if (this.cache.has(key)) {
        this.renderResults(q, this.cache.get(key));
        return;
      }
      this._abort = 'AbortController' in window ? new AbortController() : null;
      this.resultsEl.setAttribute('aria-busy', 'true');
      fetch(key, { headers: { Accept: 'application/json' }, signal: this._abort ? this._abort.signal : undefined })
        .then(function (r) {
          if (!r.ok) throw new Error('suggest_failed');
          return r.json();
        })
        .then((json) => {
          var results = (json && json.resources && json.resources.results) || {};
          this.cache.set(key, results);
          if (this.searchInput.value.trim() === q) this.renderResults(q, results);
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          this.renderResults(q, {});
        })
        .finally(() => this.resultsEl.removeAttribute('aria-busy'));
    }

    formatPrice(value) {
      var amount = parseFloat(value);
      if (isNaN(amount)) return '';
      var whole = Math.round(amount * 100) % 100 === 0;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency',
          currency: this.dataset.currency || 'GBP',
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (e) {
        return amount.toFixed(whole ? 0 : 2);
      }
    }

    /**
     * 2026-10-04: search GUIDE shortcuts (metaobject `search_guide_shortcut`,
     * embedded by snippets/search-guide-shortcut.liquid mode 'json'). Mirrors
     * the Liquid rules: whole-word/phrase match (hyphens/punctuation = spaces),
     * longest matching keyword first then position, a keyword inside a longer
     * matched keyword ignored, de-duplicated by url. Educational content only.
     */
    guideShortcuts(q) {
      if (this._guideEntries === undefined) {
        this._guideEntries = [];
        var el = this.querySelector('[data-mm-e2-guide-shortcuts]');
        try {
          if (el) this._guideEntries = JSON.parse(el.textContent || '[]') || [];
        } catch (e) {
          this._guideEntries = [];
        }
      }
      var norm = function (s) {
        return String(s || '')
          .toLowerCase()
          .replace(/[,.?!"*\-']/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      };
      var query = norm(q);
      if (!query) return [];
      var padded = ' ' + query + ' ';
      var hits = [];
      this._guideEntries.forEach(function (e) {
        var best = '';
        (e.k || []).forEach(function (kw) {
          var k = norm(kw);
          if (k && padded.indexOf(' ' + k + ' ') > -1 && k.length > best.length) best = k;
        });
        if (best && e.u && e.t) hits.push({ kw: best, url: e.u, title: e.t, pos: Number(e.p) || 0 });
      });
      hits.sort(function (a, b) {
        return b.kw.length - a.kw.length || a.pos - b.pos;
      });
      var taken = [];
      var seen = {};
      var out = [];
      hits.forEach(function (h) {
        var inside = taken.some(function (t) {
          return t !== h.kw && (' ' + t + ' ').indexOf(' ' + h.kw + ' ') > -1;
        });
        if (inside && taken.indexOf(h.kw) === -1) return;
        if (taken.indexOf(h.kw) === -1) taken.push(h.kw);
        if (seen[h.url]) return;
        seen[h.url] = true;
        var type = h.url.indexOf('/pages/concerns') === 0 ? 'Guide' : h.url.indexOf('/blogs/') === 0 ? 'Article' : 'Page';
        out.push({ title: h.title, url: h.url, type: type });
      });
      return out;
    }

    renderResults(q, results) {
      var productsLimit = parseInt(this.dataset.productsLimit, 10) || 4;
      var guidesLimit = parseInt(this.dataset.guidesLimit, 10) || 4;
      var products = (results.products || []).slice(0, productsLimit);
      var shortcuts = this.guideShortcuts(q);
      var shortcutUrls = {};
      shortcuts.forEach(function (g) {
        shortcutUrls[g.url] = true;
      });
      var notShortcut = function (g) {
        return !shortcutUrls[String(g.url || '').split('?')[0]];
      };
      // 2026-10-07 owner: collections were never requested, so searching
      // "scalp" or "men" found products but not the collection itself.
      var collections = (results.collections || []).slice(0, 3);
      var pages = (results.pages || []).map(function (p) {
        var h = p.handle || '';
        var type = h.indexOf('ingredients-') === 0 ? 'Ingredient' : h.indexOf('concerns') === 0 ? 'Guide' : 'Page';
        return { title: p.title, url: p.url, type: type };
      });
      var articles = (results.articles || []).map(function (a) {
        return { title: a.title, url: a.url, type: 'Article' };
      });
      var guides = shortcuts
        .concat(pages.concat(articles).filter(notShortcut))
        .slice(0, Math.max(guidesLimit, shortcuts.length));
      var html = '';

      if (products.length) {
        html += '<p class="mm-e2__eyebrow">Products</p><ul class="mm-e2__results-list" role="list">';
        html += products
          .map((p) => {
            var img = (p.featured_image && p.featured_image.url) || p.image || '';
            if (img) img += (img.indexOf('?') > -1 ? '&' : '?') + 'width=120';
            return (
              '<li><a class="mm-e2__result mm-e2__result--product" data-mm-e2-result href="' +
              escapeHtml(p.url) +
              '"><span class="mm-e2__result-thumb">' +
              (img
                ? '<img src="' + escapeHtml(img) + '" alt="" width="58" height="58" loading="lazy">'
                : '') +
              '</span><span class="mm-e2__result-title">' +
              highlight(p.title, q) +
              '</span><span class="mm-e2__result-aside">' +
              escapeHtml(this.formatPrice(p.price)) +
              '</span></a></li>'
            );
          })
          .join('');
        html += '</ul>';
      }
      if (collections.length) {
        html += '<p class="mm-e2__eyebrow">Collections</p><ul class="mm-e2__results-list" role="list">';
        html += collections
          .map(function (c) {
            return (
              '<li><a class="mm-e2__result" data-mm-e2-result href="' +
              escapeHtml(c.url) +
              '"><span class="mm-e2__result-title">' +
              highlight(c.title, q) +
              '</span><span class="mm-e2__result-type">Collection</span></a></li>'
            );
          })
          .join('');
        html += '</ul>';
      }
      if (guides.length) {
        html += '<p class="mm-e2__eyebrow">Ingredients and guides</p><ul class="mm-e2__results-list" role="list">';
        html += guides
          .map(function (g) {
            return (
              '<li><a class="mm-e2__result" data-mm-e2-result href="' +
              escapeHtml(g.url) +
              '"><span class="mm-e2__result-title">' +
              highlight(g.title, q) +
              '</span><span class="mm-e2__result-type">' +
              g.type +
              '</span></a></li>'
            );
          })
          .join('');
        html += '</ul>';
      }
      if (!products.length && !collections.length && !guides.length) {
        html += '<p class="mm-e2__empty">No matches for “' + escapeHtml(q) + '” yet.</p>';
      }
      html +=
        '<a class="mm-e2__see-all" data-mm-e2-see-all href="' +
        escapeHtml(this.searchPageUrl(q)) +
        '">See all results for “' +
        escapeHtml(q) +
        '”</a>';
      this.resultsEl.innerHTML = html;
    }

    /* ---------- rewards points (signed in) ---------- */
    updatePoints() {
      if (!this.pointsEl || !this.hasAttribute('data-logged-in')) return;
      var state = window.EnrichmentRewardsState;
      if (!state) return;
      var render = (data) => {
        if (!data || data.locked || data.pointsBalance == null) return;
        var n = Number(data.pointsBalance) || 0;
        this.pointsEl.textContent = n.toLocaleString('en-GB') + (n === 1 ? ' point' : ' points');
      };
      render(state.get());
      if (!this._pointsSubscribed && state.subscribe) {
        this._pointsSubscribed = true;
        state.subscribe(render);
      }
      if (state.refresh) {
        state
          .refresh(this.dataset.rewardsMeUrl || '/apps/rewards/me')
          .then(render)
          .catch(function () {});
      }
    }

    /* ---------- theme editor ---------- */
    bindThemeEditor() {
      var id = this.dataset.sectionId;
      document.addEventListener('shopify:section:select', (e) => {
        if (e.detail && e.detail.sectionId === id && !window.matchMedia(DESKTOP_MQ).matches) this.open(null);
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.detail && e.detail.sectionId === id) this.close({ returnFocus: false, immediate: true });
      });
      document.addEventListener('shopify:section:unload', (e) => {
        if (e.detail && e.detail.sectionId === id && document.body.contains(this)) {
          unlockPage();
          this.remove();
        }
      });
    }
  }

  customElements.define('mobile-menu-e2', MobileMenuE2);
})();
